/**
 * The join screen of a split-screen race, in the spirit of a kart game's character select: four
 * seats in a grid, each empty until a pad presses a button (or Enter takes the keyboard), then a
 * headband color chosen with left and right, and Jump to be ready. Two, three, or four can race;
 * when every taken seat is ready and at least two are, the course select follows, and the game
 * lays out one viewport per seat that joined. Start on a pad or Tab opens the
 * settings; Tuck leaves a seat; Back on a pad or Escape returns to the start menu. The mouse works
 * on everything too.
 *
 * Each seat reads its own devices through its own input source, so a press on player 2's pad never
 * moves player 1's choice. Devices are polled every rendered frame while the screen is open.
 */

import {
  conditioningFromTuning,
  GamepadReader,
  type GamepadRoster,
  type InputConditioning,
  InputSource,
  type KeyboardReader,
  TouchReader,
} from "@lache/input";
import { HEADBANDS, headband, type Pose, type PoseName, type RenderFeel } from "@lache/render";
import { createInputFrame, type InputFrame, type Tuning } from "@lache/sim";
import { SeatFigure } from "./figure";
import { activateMenuButton, consumed, isTyping, keyCode } from "./menu";
import { setupMenuScroll } from "./menu-scroll";
import { MAX_PLAYERS } from "./settings";

/** Stick deflection that counts as a step, and the repeat delay while held, ms. */
const STEP = 0.5;
const REPEAT_MS = 220;

export interface LobbySeat {
  /** Gamepad API index, or -1 when the seat holds the keyboard or nothing. */
  pad: number;
  keyboard: boolean;
  joined: boolean;
  ready: boolean;
  band: number;
}

export interface LobbyHost {
  roster: GamepadRoster;
  keyboard: KeyboardReader;
  tuning: Tuning;
  /** The look the seats' ninjas are drawn with (the same objects the game uses, so the F1 panel applies). */
  feel: RenderFeel;
  poses: Record<PoseName, Pose>;
  /** Every taken seat is ready, at least two are: on to the course select. */
  onReady(seats: LobbySeat[]): void;
  onSettings(): void;
  onBack(): void;
  /** A seat's headband changed (saved as a setting). */
  onBand(index: number, band: number): void;
}

interface SeatInput {
  source: InputSource;
  frame: InputFrame;
  prevJump: boolean;
  prevTuck: boolean;
  prevPause: boolean;
  prevRestart: boolean;
  prevDir: number;
  repeatAt: number;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text = "",
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = className;
  if (text) e.textContent = text;
  return e;
}

function cssColor(color: number): string {
  return `#${color.toString(16).padStart(6, "0")}`;
}

export class Lobby {
  readonly el: HTMLDivElement;
  readonly seats: LobbySeat[] = [];
  open = false;
  private readonly inputs: (SeatInput | null)[] = [];
  private readonly cards: {
    root: HTMLDivElement;
    device: HTMLDivElement;
    bandName: HTMLDivElement;
    swatches: HTMLButtonElement[];
    ready: HTMLButtonElement;
    hint: HTMLDivElement;
    figure: SeatFigure;
  }[] = [];
  private readonly status: HTMLDivElement;
  private readonly cancelScroll: () => void;
  private cond: InputConditioning;

  constructor(
    parent: HTMLElement,
    private readonly host: LobbyHost,
    bands: number[],
  ) {
    this.cond = conditioningFromTuning(host.tuning);
    this.el = el("div", "lobby");
    this.el.hidden = true;
    // The header: the title with the Settings and Back buttons beside it, in reach on a phone.
    const head = el("div", "lobby-head");
    head.append(el("div", "title", "Split-screen"));
    const actions = el("div", "lobby-actions");
    const settings = document.createElement("button");
    settings.type = "button";
    settings.className = "lobby-button";
    settings.textContent = "Settings";
    settings.addEventListener("click", (e) => {
      if (!e.defaultPrevented) this.host.onSettings();
    });
    const back = document.createElement("button");
    back.type = "button";
    back.className = "lobby-button";
    back.textContent = "Back";
    back.addEventListener("click", (e) => {
      if (!e.defaultPrevented) this.host.onBack();
    });
    actions.append(settings, back);
    head.appendChild(actions);
    this.el.appendChild(head);
    this.el.append(
      el(
        "div",
        "lobby-sub",
        "Press any button on a pad to join, or Enter for the keyboard. Two to four can race. Pick a headband, then Jump to be ready.",
      ),
    );
    const seatsEl = el("div", MAX_PLAYERS > 2 ? "seats four" : "seats");
    for (let i = 0; i < MAX_PLAYERS; i++) {
      this.seats.push({ pad: -1, keyboard: false, joined: false, ready: false, band: bands[i] ?? i });
      this.inputs.push(null);
      const root = el("div", "seat");
      root.append(el("div", "seat-name", `Player ${i + 1}`));
      // The seat's ninja on a little screen: the real character on a stage of its own (figure.ts).
      const figureEl = el("div", "seat-figure");
      const figure = new SeatFigure(figureEl, host.tuning, host.feel, host.poses);
      root.appendChild(figureEl);
      const bandName = el("div", "seat-band-name");
      const swatchRow = el("div", "seat-swatches");
      const swatches: HTMLButtonElement[] = [];
      HEADBANDS.forEach((b, k) => {
        const s = document.createElement("button");
        s.type = "button";
        s.className = "swatch";
        s.title = b.name;
        s.style.setProperty("--swatch", cssColor(b.color));
        s.addEventListener("click", (e) => {
          if (e.defaultPrevented) return;
          if (!this.seats[i]!.joined && !this.takeKeyboard(i)) return;
          this.setBand(i, k);
        });
        swatchRow.appendChild(s);
        swatches.push(s);
      });
      const device = el("div", "seat-device");
      const ready = document.createElement("button");
      ready.type = "button";
      ready.className = "seat-ready";
      ready.addEventListener("click", (e) => {
        if (e.defaultPrevented) return;
        const seat = this.seats[i]!;
        if (!seat.joined) {
          if (this.takeKeyboard(i)) this.render();
          return;
        }
        this.setReady(i, !seat.ready);
      });
      const hint = el("div", "seat-hint");
      root.append(bandName, swatchRow, device, ready, hint);
      seatsEl.appendChild(root);
      this.cards.push({ root, device, bandName, swatches, ready, hint, figure });
    }
    this.el.appendChild(seatsEl);
    this.status = el("div", "lobby-status");
    this.el.appendChild(this.status);
    this.el.append(
      el(
        "div",
        "hint",
        "left / right: headband · Jump or Enter: ready · Tuck: leave the seat · Start or Tab: settings · Back or Escape: main menu",
      ),
    );
    parent.appendChild(this.el);
    this.cancelScroll = setupMenuScroll(this.el, () => this.open);
    this.el.addEventListener("keydown", (e) => {
      if (this.open) activateMenuButton(e, this.el);
    });
    window.addEventListener("keydown", (e) => {
      if (!this.open || isTyping()) return;
      const code = keyCode(e);
      if (code !== "Enter" && code !== "NumpadEnter" && code !== "Tab" && code !== "Escape") return;
      // The Enter that picked Split-screen on the start menu opened this screen; it does not also join.
      if (consumed(e)) return;
      e.preventDefault();
      if (e.repeat) return;
      if (code === "Enter" || code === "NumpadEnter") {
        const seat = this.keyboardSeat();
        if (seat < 0) {
          const free = this.freeSeat();
          if (free >= 0) {
            this.takeKeyboard(free);
            this.render();
          }
        } else this.setReady(seat, !this.seats[seat]!.ready);
      } else if (code === "Tab") {
        this.host.onSettings();
      } else if (code === "Escape") {
        const seat = this.keyboardSeat();
        if (seat >= 0 && !this.seats[seat]!.ready) this.leave(seat);
        else if (seat < 0) this.host.onBack();
      }
    });
  }

  /** Open the screen; seats keep their devices and colors, readiness starts over unless kept. */
  show(keepReady = false): void {
    this.open = true;
    this.el.hidden = false;
    this.cond = conditioningFromTuning(this.host.tuning);
    for (let i = 0; i < this.seats.length; i++) {
      const seat = this.seats[i]!;
      if (!keepReady) seat.ready = false;
      const input = this.inputs[i];
      if (input) {
        // Every button counts as held until released, so the press that opened the screen does nothing.
        input.prevJump = true;
        input.prevTuck = true;
        input.prevPause = true;
        input.prevRestart = true;
        input.prevDir = 0;
      }
    }
    this.render();
  }

  hide(): void {
    this.cancelScroll();
    this.open = false;
    this.el.hidden = true;
    for (const card of this.cards) card.figure.pause();
  }

  /** Seats as the game will use them: the devices and the bands of every joined seat, in order. */
  joinedSeats(): LobbySeat[] {
    return this.seats.filter((s) => s.joined);
  }

  private freeSeat(): number {
    return this.seats.findIndex((s) => !s.joined);
  }

  private keyboardSeat(): number {
    return this.seats.findIndex((s) => s.joined && s.keyboard);
  }

  private padSeat(index: number): number {
    return this.seats.findIndex((s) => s.joined && s.pad === index);
  }

  private makeInput(pad: number, keyboard: boolean): SeatInput {
    const source = new InputSource(this.host.keyboard, new GamepadReader(undefined, pad), new TouchReader());
    source.keyboardEnabled = keyboard;
    return {
      source,
      frame: createInputFrame(),
      prevJump: true,
      prevTuck: true,
      prevPause: true,
      prevRestart: true,
      prevDir: 0,
      repeatAt: 0,
    };
  }

  private join(index: number, pad: number, keyboard: boolean): void {
    const seat = this.seats[index]!;
    seat.joined = true;
    seat.ready = false;
    seat.pad = pad;
    seat.keyboard = keyboard;
    this.inputs[index] = this.makeInput(pad, keyboard);
    this.render();
  }

  /** The keyboard takes seat `index` if it is free and the keyboard is not seated already. */
  private takeKeyboard(index: number): boolean {
    if (this.seats[index]!.joined || this.keyboardSeat() >= 0) return false;
    this.join(index, -1, true);
    return true;
  }

  private leave(index: number): void {
    const seat = this.seats[index]!;
    seat.joined = false;
    seat.ready = false;
    seat.pad = -1;
    seat.keyboard = false;
    this.inputs[index] = null;
    this.render();
  }

  private setBand(index: number, band: number): void {
    const seat = this.seats[index]!;
    seat.band = ((band % HEADBANDS.length) + HEADBANDS.length) % HEADBANDS.length;
    this.host.onBand(index, seat.band);
    this.render();
  }

  private setReady(index: number, ready: boolean): void {
    const seat = this.seats[index]!;
    if (!seat.joined) return;
    seat.ready = ready;
    this.render();
    const joined = this.joinedSeats();
    if (joined.length >= 2 && joined.every((s) => s.ready)) this.host.onReady(joined);
  }

  /** Poll the pads and the seats' devices; runs every rendered frame while open. */
  update(nowMs: number): void {
    if (!this.open) return;
    for (const card of this.cards) card.figure.update(nowMs);
    const roster = this.host.roster;
    roster.poll();
    for (const index of roster.pressed) {
      if (this.padSeat(index) >= 0) continue;
      const free = this.freeSeat();
      if (free >= 0) this.join(free, index, false);
    }
    let changed = false;
    for (let i = 0; i < this.seats.length; i++) {
      const seat = this.seats[i]!;
      const input = this.inputs[i];
      if (!seat.joined || !input) continue;
      // A pad that went away un-readies its seat and says so; it keeps the seat for when it returns.
      if (seat.pad >= 0) {
        const connected = roster.pads.some((p) => p.index === seat.pad && p.connected);
        if (!connected && seat.ready) {
          seat.ready = false;
          changed = true;
        }
      }
      const f = input.frame;
      input.source.sample(f, this.cond, nowMs);
      const jumpPressed = f.jump && !input.prevJump;
      const tuckPressed = f.tuck && !input.prevTuck;
      const restartPressed = f.restart && !input.prevRestart;
      // Start on a pad opens the settings; the keyboard's Escape is handled with the keys (it is Back).
      const padPause = input.source.gamepad.frame.pause;
      const pausePressed = padPause && !input.prevPause;
      input.prevJump = f.jump;
      input.prevTuck = f.tuck;
      input.prevRestart = f.restart;
      input.prevPause = padPause;
      if (isTyping()) continue;
      const steering = f.moveY > STEP || f.moveY < -STEP;
      if (pausePressed) {
        this.host.onSettings();
        return;
      }
      if (restartPressed) {
        if (seat.ready) this.setReady(i, false);
        else this.leave(i);
        continue;
      }
      if (tuckPressed && !steering) {
        if (seat.ready) this.setReady(i, false);
        else this.leave(i);
        continue;
      }
      if (jumpPressed && !steering) {
        this.setReady(i, !seat.ready);
        continue;
      }
      const dir = f.moveX > STEP ? 1 : f.moveX < -STEP ? -1 : 0;
      if (dir !== 0 && !seat.ready && (dir !== input.prevDir || nowMs >= input.repeatAt)) {
        this.setBand(i, seat.band + dir);
        input.repeatAt = nowMs + (dir !== input.prevDir ? REPEAT_MS * 2 : REPEAT_MS);
      }
      input.prevDir = dir;
    }
    if (changed) this.render();
  }

  private deviceLabel(seat: LobbySeat): string {
    if (!seat.joined) return "press a button on a pad, or Enter";
    if (seat.keyboard) return "keyboard";
    const connected = this.host.roster.pads.some((p) => p.index === seat.pad && p.connected);
    return connected
      ? this.host.roster.label(seat.pad)
      : `${this.host.roster.label(seat.pad)} (disconnected)`;
  }

  private render(): void {
    for (let i = 0; i < this.seats.length; i++) {
      const seat = this.seats[i]!;
      const card = this.cards[i]!;
      const band = headband(seat.band);
      card.root.classList.toggle("joined", seat.joined);
      card.root.classList.toggle("ready", seat.ready);
      card.root.style.setProperty("--band", cssColor(band.color));
      card.figure.setAccent(band.color);
      card.bandName.textContent = band.name;
      card.swatches.forEach((s, k) => {
        s.classList.toggle("selected", k === seat.band);
      });
      card.device.textContent = this.deviceLabel(seat);
      card.ready.textContent = !seat.joined ? "Join with the keyboard" : seat.ready ? "Ready ✓" : "Ready";
      card.ready.hidden = !seat.joined && this.keyboardSeat() >= 0;
      card.hint.textContent = !seat.joined
        ? ""
        : seat.ready
          ? "Tuck: not ready"
          : "left / right: headband · Jump: ready · Tuck: leave";
    }
    const joined = this.joinedSeats();
    const waiting = joined.filter((s) => !s.ready).length;
    this.status.textContent =
      joined.length < 2
        ? "Waiting for two players. Connect another pad, or one player takes the keyboard."
        : waiting === 0
          ? `All ${joined.length} ready: picking a course.`
          : waiting === joined.length
            ? `${joined.length} in: press Jump when ready.`
            : `Waiting for ${waiting} more to press Jump.`;
  }
}
